/**
 * SCOPE-PARSE-TOGGLES 2026-09-17 — the parsing switches of the blogger card.
 *
 * ONE MODEL, THREE SWITCHES. An account is parsed iff
 * `Pack.parsingEnabled AND TrackedAccount.monitoringEnabled`
 * (docs/LOGIC-MANIFEST.ru.md; metrics-parser/src/scan/syncEligibility.js), and the
 * blogger-wide `Lead.monitoringPaused` beats both. Before this slice those three bits
 * were written by five different buttons in two places — «ПАРСИМ да / Выключить
 * парсинг» and «Выключить/Включить» per account inside ⚙ «Настройки пачки», plus
 * «Выключить блогера» on the tab header — so a manager could not SEE the state without
 * opening a dialog, and a pack that was ON with every account OFF read as «ПАРСИМ да».
 * Each bit now has exactly one control, on the surface that shows the bit, and the
 * dialog keeps only what is not a switch («Заменить / Убрать / + Добавить»).
 *
 * MISCLICK GUARD. The two wide switches — the pack and the blogger — arm on the first
 * click and apply on the second, inside a 3-second window; letting the window lapse is
 * the cancel. It replaces the `window.confirm()` those two controls used to raise (the
 * account rows had one too and lose it: one account is not a wide blast radius). Only
 * the OFF direction arms — turning parsing back ON is the safe direction and the
 * inline prompt («Выключить? нажмите ещё раз») says which one this is.
 *
 * OPTIMISM lives in the mutation hooks (api/packs.js `optimisticPacks`), not here: the
 * pack envelope is one queryKey feeding every switch on the card, so one cache write
 * moves the whole picture and one rollback restores it.
 *
 * MONITORING2 2026-09-23 (spec §3.3) — THE FOLD. The card shows only the account switch
 * (the pack and blogger switches below stay exported, unmounted). It shows the EFFECTIVE
 * bit `Pack.parsingEnabled AND TrackedAccount.monitoringEnabled`, and it is live under a
 * shut pack: turning one account ON there makes the route fold the pack in one TX — the
 * pack comes on, its other accounts go off, this account comes on — so exactly one account
 * resumes. Turning an account OFF writes that account only; the pack row is never touched.
 */
import { useEffect, useState } from 'react';
import { useLeadMonitoringPaused, useSetLeadMonitoringPause, useSetPackAccountMonitoring } from '../api/packs.js';
import { useSetPackDisabled } from '../api/rounds.js';
import { Toggle } from './ds';

const ARM_MS = 3000;

function useArmedOff (run) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), ARM_MS);
    return () => clearTimeout(t);
  }, [armed]);

  const fire = (next) => {
    if (next) { setArmed(false); run(true); return; }
    if (!armed) { setArmed(true); return; }
    setArmed(false);
    run(false);
  };

  return [armed, fire];
}

export const PARSED_PLATFORMS = new Set(['tiktok', 'instagram', 'youtube', 'facebook']);

/**
 * «Парсинг» — the pack half of the two-switch rule (Pack.parsingEnabled). The same
 * PATCH still writes `endedManuallyAt` alongside the flag, so the four readers that
 * render «остановлена вручную» keep their stamp.
 *
 * The count beside it is the whole point of moving the control out of the dialog: it
 * reads the OTHER half of the rule, so a pack that is ON with every account OFF says
 * «парсится 0 из 4 акк.» instead of «ПАРСИМ да».
 */
export function PackParsingToggle ({ leadId, pack }) {
  const mut = useSetPackDisabled();
  const on = pack?.parsingEnabled !== false;
  const accounts = (pack?.accounts ?? []).filter((a) => PARSED_PLATFORMS.has(a.platform) && a.status === 'active');
  const live = accounts.filter((a) => a.monitoringEnabled !== false).length;
  const [armed, fire] = useArmedOff((next) => mut.mutate({ packId: pack.id, disabled: !next, leadId }));

  return (
    <span className="oc-lead-card__parse">
      <Toggle on={on} disabled={mut.isPending} onChange={fire}>Парсинг</Toggle>
      {armed ? (
        <span className="oc-lead-card__parse-arm">Выключить? нажмите ещё раз</span>
      ) : on ? (
        <span className="oc-lead-card__muted">парсится {live} из {accounts.length} акк.</span>
      ) : (
        <span className="oc-lead-card__muted">парсинг выключен</span>
      )}
      {mut.isError && <span className="oc-lead-card__error">Не удалось переключить парсинг пачки.</span>}
    </span>
  );
}

/**
 * The account half (TrackedAccount.monitoringEnabled), one switch per account in the
 * card's «Парсинг» list. No arming: a single account is not a wide blast radius, and the
 * switch is read back from the same envelope it writes.
 *
 * It renders the EFFECTIVE bit — off whenever the pack gate is shut. Only a banned
 * account renders disabled (the route refuses enabling with 409 `account_banned`); under
 * a shut pack the switch stays clickable and ON folds the pack (header). The reason is a
 * `title` on the wrapper, not on the switch: the adherence gate's `Toggle` allowlist
 * carries no `title`.
 */
export function AccountParsingToggle ({ leadId, packId, account, packOn }) {
  const mut = useSetPackAccountMonitoring();
  const banned = account.status === 'banned';
  const on = packOn && account.monitoringEnabled !== false && !banned;
  const reason = banned ? 'аккаунт забанен' : !packOn ? 'пачка выключена — включение запустит только этот аккаунт' : undefined;

  return (
    <span className="oc-lead-card__acc-toggle" title={reason}>
      <Toggle
        on={on}
        disabled={banned || mut.isPending}
        onChange={(next) => mut.mutate({ id: packId, leadId, accountId: account.id, enabled: next })}
      />
      {mut.isError && <span className="oc-lead-card__error">не переключился</span>}
    </span>
  );
}

/**
 * Blogger-wide kill switch (Lead.monitoringPaused) — the parser's eligibility sync
 * turns off EVERY bridged account of the lead while paused, beating pack phases. The
 * count beside it reads the pack half, the same way the pack switch reads the account
 * half.
 */
export function BloggerMonitoringToggle ({ leadId, packs = [] }) {
  const pausedQ = useLeadMonitoringPaused(leadId);
  const mut = useSetLeadMonitoringPause();
  const on = pausedQ.data !== true;
  const livePacks = packs.filter((p) => p.parsingEnabled !== false).length;
  const [armed, fire] = useArmedOff((next) => mut.mutate({ leadId, paused: !next }));

  return (
    <div className="oc-lead-card__pause">
      <Toggle
        on={on}
        disabled={mut.isPending || pausedQ.isLoading}
        onChange={fire}
      >Мониторинг блогера</Toggle>
      {armed ? (
        <span className="oc-lead-card__parse-arm">Выключить? нажмите ещё раз</span>
      ) : on ? (
        <span className="oc-lead-card__muted">включено {livePacks} из {packs.length} пачек</span>
      ) : (
        <span className="oc-lead-card__muted">выключены все пачки и аккаунты</span>
      )}
      {mut.isError && <span className="oc-lead-card__error">Не удалось переключить.</span>}
    </div>
  );
}
